import {
  CALENDAR_UPCOMING_VIEW,
  type CalendarOccurrence,
  CalendarUpcomingViewSchema,
} from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useSourceView } from '../_sdk/hooks';
import { defineWidget } from '../_sdk/types';
import { inWindow } from './model';

/**
 * Agenda (PRD VS-042): the `upcoming` view a `calendar-ics` source publishes at
 * each poll (now − 1 h → now + 14 days), shown as today, this week or a list.
 */

export const PropsSchema = z.object({
  label: z.string().max(80).optional(),
  sourceId: z.uuid(),
  view: z.enum(['day', 'week', 'list']).default('list'),
  /** List view horizon; the source publishes 14 days. */
  days: z.number().int().min(1).max(14).default(7),
});

export type CalendarProps = z.output<typeof PropsSchema>;
export type CalendarData = CalendarOccurrence[];

export default defineWidget<CalendarProps, CalendarData>({
  type: 'calendar',
  category: 'event',
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 3, h: 3 },
  propsSchema: PropsSchema,
  defaults: { view: 'list', days: 7 },
  sourceIds: (p) => [p.sourceId],
  useData: (p) => {
    const query = useSourceView(p.sourceId, CALENDAR_UPCOMING_VIEW, CalendarUpcomingViewSchema);
    const updatedAt = query.data?.updatedAt;
    return toDataState(query, {
      select: (view) => (view ? inWindow(view.data, p.view, p.days, Date.now()) : []),
      isEmpty: (entries) => entries.length === 0,
      // The stale badge tells the age of the view, not of the next meeting.
      lastUpdated: () => (updatedAt ? new Date(updatedAt) : undefined),
    });
  },
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
